import { useMemo, useState } from 'react';
import { TYPE_COLORS_BG, TYPE_COLORS_FG } from '../constants';
import { S } from '../strings';
import type { ParsedItem, UpdateType } from '../types';

type Props = {
  items: ParsedItem[];
  onItemClick: (item: ParsedItem) => void;
  // Muted items only arrive here when "Näytä kaikki" is on; pills are dimmed.
  isMuted?: (id: string) => boolean;
  quartersAhead?: number;
};

const MONTHS_FI_SHORT = [
  'Tam',
  'Hel',
  'Maa',
  'Huh',
  'Tou',
  'Kes',
  'Hei',
  'Elo',
  'Syy',
  'Lok',
  'Mar',
  'Jou',
];
const MAX_VISIBLE_PER_CELL = 10;

// Phase C roadmap — quarter-grid forward view. Phase B made this meaningful:
// every item on the grid has an AI-extracted (or title-parsed) effectiveDate
// in the future. Items whose effectiveDate fell through to publishedDate are
// excluded, so the roadmap shows real roadmap content rather than "a week
// of RSS activity bucketed by day."
const BEYOND_KEY = '__beyond__';

export function Roadmap({ items, onItemClick, isMuted, quartersAhead = 5 }: Props) {
  const now = useMemo(() => new Date(), []);

  const quarters = useMemo(() => {
    const out: { key: string; label: string; year: number; q: number; isCurrent: boolean }[] = [];
    const currentQ = Math.floor(now.getMonth() / 3) + 1;
    const currentY = now.getFullYear();
    for (let offset = 0; offset <= quartersAhead; offset++) {
      const totalQ = (currentQ - 1 + offset) % 4;
      const year = currentY + Math.floor((currentQ - 1 + offset) / 4);
      const q = totalQ + 1;
      out.push({
        key: `${year}-Q${q}`,
        label: `Q${q} ${year}`,
        year,
        q,
        isCurrent: offset === 0,
      });
    }
    return out;
  }, [now, quartersAhead]);

  const { buckets, beyond } = useMemo(() => {
    const b: Record<string, ParsedItem[]> = {};
    for (const q of quarters) b[q.key] = [];
    const beyondArr: ParsedItem[] = [];
    const nowMs = now.getTime();
    for (const item of items) {
      if (!item.effectiveDate) continue;
      const d = new Date(item.effectiveDate);
      const t = d.getTime();
      // Future-only: guarantees the date came from AI / title regex / summary
      // rather than the publishedDate fallback (which is always <= today).
      if (isNaN(t) || t <= nowMs) continue;
      const year = d.getFullYear();
      const q = Math.floor(d.getMonth() / 3) + 1;
      const key = `${year}-Q${q}`;
      if (key in b) {
        b[key].push(item);
      } else {
        // Items past the visible window land in the "Myöhemmin" overflow column
        // so long-horizon retirements (e.g. AKS Ubuntu 22.04 at Q2 2027) don't
        // vanish without a trace.
        beyondArr.push(item);
      }
    }
    for (const k of Object.keys(b)) {
      b[k].sort((a, b) => new Date(a.effectiveDate!).getTime() - new Date(b.effectiveDate!).getTime());
    }
    beyondArr.sort(
      (a, b) => new Date(a.effectiveDate!).getTime() - new Date(b.effectiveDate!).getTime(),
    );
    return { buckets: b, beyond: beyondArr };
  }, [items, quarters, now]);

  const totalItems =
    Object.values(buckets).reduce((s, arr) => s + arr.length, 0) + beyond.length;
  const showBeyondColumn = beyond.length > 0;

  return (
    <div
      style={{
        background: 'var(--surface, #F8FAFC)',
        border: '1px solid var(--border, #E5E7EB)',
        borderRadius: 12,
        padding: 18,
        marginBottom: 22,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 14,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text, #111827)' }}>
            {S.roadmap.title}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted, #6B7280)' }}>
            {totalItems} {totalItems === 1 ? 'tulossa' : 'tulossa'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, fontSize: 11, flexWrap: 'wrap' }}>
          <LegendDot type="ga" label={S.roadmap.legend.ga} />
          <LegendDot type="preview" label={S.roadmap.legend.preview} />
          <LegendDot type="rollout" label={S.roadmap.legend.other} />
          <LegendDot type="deprecation" label={S.roadmap.legend.deprecation} />
        </div>
      </div>

      {totalItems === 0 ? (
        <div
          style={{
            padding: '40px 8px',
            textAlign: 'center',
            color: 'var(--text-muted, #9CA3AF)',
            fontSize: 13,
            fontStyle: 'italic',
          }}
        >
          {S.roadmap.empty}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${quarters.length}, minmax(0, 1fr))${showBeyondColumn ? ' minmax(0, 1fr)' : ''}`,
            border: '1px solid var(--border-soft, rgba(148,163,184,0.25))',
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          {quarters.map((qDef, idx) => (
            <div
              key={qDef.key}
              style={{
                padding: '10px 14px',
                fontSize: 12,
                fontWeight: qDef.isCurrent ? 800 : 700,
                color: qDef.isCurrent ? 'var(--accent, #0078D4)' : 'var(--text, #1F2937)',
                background: qDef.isCurrent
                  ? 'var(--accent-soft, rgba(0,120,212,0.08))'
                  : 'var(--surface-muted, rgba(148,163,184,0.06))',
                borderBottom: '1px solid var(--border-soft, rgba(148,163,184,0.25))',
                borderRight:
                  idx === quarters.length - 1 && !showBeyondColumn
                    ? 'none'
                    : '1px solid var(--border-soft, rgba(148,163,184,0.25))',
              }}
            >
              {qDef.label}
            </div>
          ))}
          {showBeyondColumn && (
            <div
              style={{
                padding: '10px 14px',
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--text-muted, #6B7280)',
                background: 'var(--surface-muted, rgba(148,163,184,0.06))',
                borderBottom: '1px solid var(--border-soft, rgba(148,163,184,0.25))',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {S.roadmap.beyondLabel}
              <span
                style={{
                  background: 'rgba(148,163,184,0.25)',
                  color: 'var(--text, #1F2937)',
                  fontSize: 10,
                  fontWeight: 800,
                  padding: '1px 6px',
                  borderRadius: 8,
                }}
              >
                {beyond.length}
              </span>
            </div>
          )}

          {quarters.map((qDef, idx) => (
            <QuarterCell
              key={`cell-${qDef.key}`}
              items={buckets[qDef.key]}
              isCurrent={qDef.isCurrent}
              isLastCol={idx === quarters.length - 1 && !showBeyondColumn}
              onItemClick={onItemClick}
              isMuted={isMuted}
            />
          ))}
          {showBeyondColumn && (
            <QuarterCell
              key={`cell-${BEYOND_KEY}`}
              items={beyond}
              isCurrent={false}
              isLastCol={true}
              onItemClick={onItemClick}
              isMuted={isMuted}
              showYearOnDate
            />
          )}
        </div>
      )}
    </div>
  );
}

function QuarterCell({
  items,
  isCurrent,
  isLastCol,
  onItemClick,
  isMuted,
  showYearOnDate = false,
}: {
  items: ParsedItem[];
  isCurrent: boolean;
  isLastCol: boolean;
  onItemClick: (item: ParsedItem) => void;
  isMuted?: (id: string) => boolean;
  showYearOnDate?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, MAX_VISIBLE_PER_CELL);
  const overflow = items.length - MAX_VISIBLE_PER_CELL;

  return (
    <div
      style={{
        padding: 10,
        minHeight: 96,
        background: isCurrent ? 'var(--accent-soft, rgba(0,120,212,0.03))' : undefined,
        borderRight: isLastCol
          ? 'none'
          : '1px solid var(--border-soft, rgba(148,163,184,0.2))',
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
      }}
    >
      {visible.map((item) => (
        <ItemPill
          key={item.id}
          item={item}
          onClick={() => onItemClick(item)}
          muted={!!isMuted?.(item.id)}
          showYear={showYearOnDate}
        />
      ))}
      {!expanded && overflow > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '3px 6px',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--accent, #0078D4)',
            cursor: 'pointer',
            textAlign: 'left',
            alignSelf: 'flex-start',
            fontFamily: 'inherit',
          }}
        >
          {S.roadmap.more(overflow)}
        </button>
      )}
      {items.length === 0 && (
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-muted, #9CA3AF)',
            fontStyle: 'italic',
            paddingTop: 4,
          }}
        >
          —
        </div>
      )}
    </div>
  );
}

function ItemPill({
  item,
  onClick,
  muted = false,
  showYear = false,
}: {
  item: ParsedItem;
  onClick: () => void;
  muted?: boolean;
  showYear?: boolean;
}) {
  const isRetirement = item.updateType === 'deprecation';
  const bg = TYPE_COLORS_BG[item.updateType];
  const fg = TYPE_COLORS_FG[item.updateType];
  const d = new Date(item.effectiveDate!);
  const monthLabel = showYear
    ? `${MONTHS_FI_SHORT[d.getMonth()]} ${d.getFullYear()}`
    : `${MONTHS_FI_SHORT[d.getMonth()]} ${d.getDate()}`;
  const label = shortLabel(item.title);

  return (
    <button
      type="button"
      onClick={onClick}
      title={`${item.title} · ${d.toLocaleDateString('fi-FI')}`}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        alignItems: 'center',
        columnGap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        background: bg,
        color: fg,
        fontSize: 11,
        fontWeight: 700,
        textDecoration: 'none',
        border: isRetirement ? `1px solid ${fg}55` : '1px solid transparent',
        lineHeight: 1.3,
        textAlign: 'left',
        overflow: 'hidden',
        cursor: 'pointer',
        fontFamily: 'inherit',
        opacity: muted ? 0.5 : 1,
        transition: 'transform 0.1s, box-shadow 0.1s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateX(2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateX(0)';
      }}
    >
      <span
        style={{
          fontSize: 9,
          fontWeight: 800,
          color: fg,
          opacity: 0.75,
          letterSpacing: 0.3,
          whiteSpace: 'nowrap',
        }}
      >
        {monthLabel}
      </span>
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}
      >
        {isRetirement && <span aria-hidden>⚠</span>}
        {label}
      </span>
    </button>
  );
}

function LegendDot({ type, label }: { type: UpdateType; label: string }) {
  const bg = TYPE_COLORS_BG[type];
  const fg = TYPE_COLORS_FG[type];
  const isRetirement = type === 'deprecation';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 8px',
        borderRadius: 999,
        background: bg,
        color: fg,
        fontSize: 10,
        fontWeight: 700,
      }}
    >
      {isRetirement && <span aria-hidden>⚠</span>}
      {label}
    </span>
  );
}

function shortLabel(title: string): string {
  const stripped = title
    .replace(/^\[[^\]]*\]\s*/, '')
    .replace(
      /^(retirement|launched|generally available|rolling out|in development|in preview|update|announcement)\s*[:–-]\s*/i,
      '',
    )
    .replace(/^microsoft\s+/i, '')
    .trim();
  if (stripped.length <= 48) return stripped;
  const words = stripped.split(/\s+/).slice(0, 6);
  return words.join(' ') + '…';
}
